import styled from "styled-components";
import { useCallback } from "react";

import { Button } from "@otta/design";
import { Company } from "@otta/search/schema";
import { Icon } from "@otta/icons";
import { pxToRem } from "@otta/design-tokens";
import { Link } from "@otta/search/components/Link";
import { pushAnalyticsEvent } from "@otta/analytics";
import { addPossessiveSuffix } from "@otta/search/utils/strings";

const Wrapper = styled.div`
  margin-top: ${pxToRem(24)};
`;

const PrimaryButton = styled(Button).attrs({
  level: "primary",
})`
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} ${pxToRem(20)};
  text-align: left;

  &:hover {
    text-decoration: none;
  }
`;

const SecondaryButton = styled(Button).attrs({
  level: "secondary",
  size: "small",
})`
  padding: ${pxToRem(7)} ${pxToRem(16)};
  text-align: left;

  &:hover {
    text-decoration: none;
  }
`;

interface CompanyProfileButtonProps {
  company: Pick<Company.Fragment, "name" | "urlSafeName">;
  level?: "primary" | "secondary";
  section: string;
}

export function CompanyProfileButton({
  company: { name, urlSafeName },
  section,
  level = "primary",
}: CompanyProfileButtonProps) {
  const url = `/companies/${encodeURIComponent(urlSafeName)}`;
  const text = `View ${addPossessiveSuffix(name)} company profile`;

  const onClick = useCallback(() => {
    pushAnalyticsEvent({
      eventName: "Candidate Clicked",
      name: "view-company-profile",
      section,
    });
  }, [section]);

  if (level === "primary") {
    return (
      <Wrapper>
        <PrimaryButton
          as={Link}
          to={url}
          target="_blank"
          onClick={() => onClick()}
        >
          {text}
          <Icon icon="arrow-right" size={2} />
        </PrimaryButton>
      </Wrapper>
    );
  }

  return (
    <SecondaryButton
      as={Link}
      to={url}
      target="_blank"
      onClick={() => onClick()}
    >
      {text}
    </SecondaryButton>
  );
}
